/** @jsxImportSource @emotion/react */

import { useSnackbar } from 'contexts';
import { SummaryCards } from 'components';
import { PageAction, ProductClassification } from 'enums';
import { FC, useEffect, useState } from 'react';
import * as styles from './ProductStatusLabel.styles';
import { Hidden as MuiHidden } from '@material-ui/core';
import ProductStatusLabels from './ProductStatusLabels';
import useProductConfigFunctions from 'utils/useProductConfigFunctions';
import { ProductConfig } from 'types/interfaces/merchant/ProductConfig';
import {
  currencyFormatter,
  getDescriptionForInterestFreeMonths,
  logEvent,
} from 'utils';
import SortableTable, { SortableTableColumn } from 'components/sortable-table';
import { ProductName } from './TableTypesAndConstants';

interface ProductsAndOffersTableProps {
  selectedProduct?: ProductClassification;
}

const ProductsAndOffersTable: FC<ProductsAndOffersTableProps> = ({
  selectedProduct: selectedProductClass,
}: ProductsAndOffersTableProps) => {
  const Snackbar = useSnackbar();

  const trackSortingEvent = (
    columnName: string,
    sortDirection: 'asc' | 'desc'
  ) => {
    logEvent(PageAction.productsAndOffersTableSorting, {
      column: columnName,
      sortDirection: sortDirection,
    });
  };

  const columns: SortableTableColumn<ProductConfig>[] = [
    {
      sortable: true,
      name: 'Classification',
      makeColumn: () => (
        <div className="body1" css={styles.tableHeader}>
          {'Product'}
        </div>
      ),
      makeCell: (p) => (
        <div className="body2" css={styles.tableCell}>
          {ProductName.get(p.Classification)}
        </div>
      ),
      customComparer: (lhs, rhs) =>
        productClassificationComparer(lhs.Classification, rhs.Classification),
      onSort: trackSortingEvent,
    },
    {
      sortable: false,
      name: 'InterestFreeMonths',
      makeColumn: () => (
        <div className="body1" css={styles.tableHeader}>
          {'Interest free offer'}
        </div>
      ),
      makeCell: (p) => (
        <div className="body2" css={styles.tableCell}>
          {getDescriptionForInterestFreeMonths(
            p.Classification,
            p.InterestFreeMonths
          )}
        </div>
      ),
    },
    {
      sortable: true,
      name: 'TransactionLimitMin',
      makeColumn: () => (
        <div className="body1" css={styles.tableHeader}>
          {'Min. transaction'}
        </div>
      ),
      makeCell: (p) => (
        <div className="body2" css={styles.tableCell}>
          {currencyFormatter(p.TransactionLimitMin)}
        </div>
      ),
      onSort: trackSortingEvent,
    },
    {
      sortable: true,
      name: 'TransactionLimitMax',
      makeColumn: () => (
        <div className="body1" css={styles.tableHeader}>
          {'Max. transaction'}
        </div>
      ),
      makeCell: (p) => (
        <div className="body2" css={styles.tableCell}>
          {currencyFormatter(p.TransactionLimitMax)}
        </div>
      ),
      onSort: trackSortingEvent,
    },
    {
      sortable: false,
      makeColumn: () => (
        <div className="body1" css={styles.tableHeader}>
          {'Active status'}
        </div>
      ),
      makeCell: (p) => <ProductStatusLabels product={p} />,
    },
  ];

  const { getProductConfigAsync } = useProductConfigFunctions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<ProductConfig[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const results = await getProductConfigAsync();
        setProducts(results || []);
      } catch (err) {
        Snackbar.error('Something went wrong');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const getFilteredData = (
    productConfigs: ProductConfig[]
  ): ProductConfig[] => {
    const data = productConfigs
      ?.filter((config) => {
        const include =
          selectedProductClass === undefined ||
          config.Classification === selectedProductClass;
        return include;
      })
      .sort((lhs, rhs) => {
        const comp = productClassificationComparer(
          lhs.Classification,
          rhs.Classification
        );
        return comp;
      });

    return data;
  };

  return (
    <>
      <MuiHidden smDown>
        <SortableTable
          columns={columns}
          isLoading={isLoading}
          data={getFilteredData(products)}
          emptyMessage="No products or offers found. "
        />
      </MuiHidden>
      <MuiHidden mdUp>
        {getFilteredData(products).map((p, idx) => (
          <SummaryCards.ProductConfig
            key={`product-config-${idx}`}
            productConfig={p}
          />
        ))}
      </MuiHidden>
    </>
  );
};

const productClassificationComparer = (
  lhs: ProductClassification,
  rhs: ProductClassification
): number => {
  const getOrdinal = (p: ProductClassification): number => {
    switch (p) {
      case ProductClassification.ZipMoney:
        return 0;
      case ProductClassification.ZipPlus:
        return 1;
      case ProductClassification.ZipPay:
        return 2;
      default:
        throw new Error(`Unsupported type ${p} in ProductsAndOffersTable`);
    }
  };
  const lhsOrdinal = getOrdinal(lhs);
  const rhsOrdinal = getOrdinal(rhs);
  return lhsOrdinal - rhsOrdinal;
};

export default ProductsAndOffersTable;
